import { callingCampaigns } from '@markku/db'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'
import type { AcquisitionData } from './acquisition'
import { audienceFilterSchema } from './audienceFilter'

// UTM identifiers stamped onto visits booked from a calling campaign. Kept
// as constants so the frontend, the historical-data backfill SQL, and the
// stats JSON query all agree on the spelling — drift between them silently
// turns "Käyntejä varattu" into 0.
export const CALLING_CAMPAIGN_UTM_SOURCE = 'call_campaign'
export const CALLING_CAMPAIGN_UTM_MEDIUM = 'outbound_call'

// Field name (under utmParams) for the numeric calling-campaign id.
// Deliberately namespaced as `call_campaign_id` rather than the generic
// `campaign_id` so future systems (email campaigns, marketing campaigns)
// can each carry their own id without colliding in the same jsonb query.
export const CALLING_CAMPAIGN_ID_PARAM = 'call_campaign_id'

// Builds the marketing-shaped acquisitionData for a visit booked off a
// calling campaign. Slots into visit.internalAcquisitionData via the
// existing VisitBookingContext.acquisitionData wiring; downstream
// `deriveSource` reads `utm_source` and surfaces the visit as a
// `call_campaign` channel in the broader attribution report.
export function buildCallingCampaignAcquisitionData(args: {
  campaignId: number
  campaignName: string
  timestamp?: string
}): AcquisitionData {
  return {
    timestamp: args.timestamp ?? new Date().toISOString(),
    utmParams: {
      utm_source: CALLING_CAMPAIGN_UTM_SOURCE,
      utm_medium: CALLING_CAMPAIGN_UTM_MEDIUM,
      utm_campaign: args.campaignName,
      [CALLING_CAMPAIGN_ID_PARAM]: String(args.campaignId),
    },
  }
}

export const DEFAULT_PRE_CALL_SMS_TEMPLATE =
  'Hei {customer.firstName}! {caller.firstName} Markku.fi:ltä soittaa sinulle pian numerosta {caller.phoneNumber}'

export interface PreCallSmsVars {
  customer: { firstName: string }
  caller: { firstName: string; phoneNumber: string }
}

// Whitelisted variable substitution. The cleanup pass scrubs artefacts that
// empty values leave behind ("Hei !" → "Hei!", double spaces).
export function renderPreCallSmsTemplate(
  template: string,
  vars: PreCallSmsVars,
): string {
  const lookup: Record<string, string> = {
    'customer.firstName': vars.customer.firstName,
    'caller.firstName': vars.caller.firstName,
    'caller.phoneNumber': vars.caller.phoneNumber,
  }
  const substituted = template.replace(/\{([a-zA-Z.]+)\}/g, (match, key) => {
    return key in lookup ? lookup[key] : match
  })
  return substituted
    .replace(/ +!/g, '!')
    .replace(/ +,/g, ',')
    .replace(/ +/g, ' ')
    .trim()
}

export const selectCallingCampaignSchema = createSelectSchema(callingCampaigns)
export const insertCallingCampaignSchema = createInsertSchema(
  callingCampaigns,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
})

export const upsertCallingCampaignSchema = z.object({
  name: z.string().min(1),
  description: z.string().nullable().optional(),
  audienceFilter: audienceFilterSchema,
  preCallSmsTemplate: z.string().nullable().optional(),
})

export const phoneCallOutcomeSchema = z.enum([
  'no_answer',
  'reached_not_interested',
  'reached_callback_later',
  'booked_visit',
  'do_not_call',
  'unclear',
  'abandoned',
])

export const phoneCallOutcomeSourceSchema = z.enum(['ai', 'operator', 'auto'])

// Reason modifier on callback_later dispositions. The UI prompts for it
// every time; nullable in storage only for rows written before this
// prompt existed.
export const phoneCallCallbackReasonSchema = z.enum([
  'no_answer',
  'customer_requested_later',
  'no_slots_available',
])

// Row shape returned by `callingCampaign.getQueue` — one lead per row.
export const callingQueueItemSchema = z.object({
  userId: z.number(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  customerId: z.number().nullable(),
  customerName: z.string().nullable(),
  propertyId: z.number().nullable(),
  propertyAddress: z.string().nullable(),
  lastCallId: z.number().nullable(),
  lastCallAt: z.string().nullable(),
  lastCallOutcome: phoneCallOutcomeSchema.nullable(),
  lastCallOutcomeSource: phoneCallOutcomeSourceSchema.nullable(),
  lastCallAiSuggestedOutcome: phoneCallOutcomeSchema.nullable(),
  lastCallAiOutcomeConfidence: z.number().nullable(),
  lastCallAiSummary: z.string().nullable(),
  lastCallAiSuggestedCallbackAt: z.string().nullable(),
  lastCallCallbackAt: z.string().nullable(),
  lastCallCallbackReason: phoneCallCallbackReasonSchema.nullable(),
  lastCallOperatorNotes: z.string().nullable(),
  // Per-campaign counters. Attempts count across the whole lifetime of this
  // lead in this campaign (no sliding window) — they drive the "already
  // called N times" warning in the callback modal and the no_answer cap that
  // auto-promotes to `abandoned`.
  noAnswerAttempts: z.number(),
  campaignAttempts: z.number(),
  // When the lead will re-surface in the calling queue. Set for
  // reached_callback_later (= callback_at) and for no_answer in cooldown
  // (= started_at + backoff interval). NULL for everyone else.
  nextRetryAt: z.string().nullable(),
})

// Lead-card context: active subs + open opportunities + every customer the
// lead is linked to. An isännöitsijä is a member of their employing agency
// plus many managed taloyhtiöt — `linkedCustomers` carries all of them so the
// workspace shows the full portfolio, not just one. Past visits are fetched
// client-side via `visit.list`.
export const leadContextSchema = z.object({
  activeSubscriptions: z.array(
    z.object({
      id: z.number(),
      displayName: z.string(),
      status: z.string(),
    }),
  ),
  openOpportunities: z.array(
    z.object({
      id: z.number(),
      stage: z.string(),
      contextName: z.string().nullable(),
    }),
  ),
  linkedCustomers: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      type: z.string(),
      accessRole: z.enum(['owner', 'member']).nullable(),
      relationshipType: z
        .enum(['property_manager', 'board_chair', 'board', 'other'])
        .nullable(),
    }),
  ),
})

export type CallingCampaign = z.infer<typeof selectCallingCampaignSchema>
export type InsertCallingCampaign = z.infer<typeof insertCallingCampaignSchema>
export type UpsertCallingCampaign = z.infer<typeof upsertCallingCampaignSchema>
export type CallingQueueItem = z.infer<typeof callingQueueItemSchema>
export type LeadContext = z.infer<typeof leadContextSchema>
export type PhoneCallOutcome = z.infer<typeof phoneCallOutcomeSchema>
export type PhoneCallOutcomeSource = z.infer<
  typeof phoneCallOutcomeSourceSchema
>
export type PhoneCallCallbackReason = z.infer<
  typeof phoneCallCallbackReasonSchema
>
